import React, { useEffect, useState } from 'react'
import Dialog from '../../../../../../../common/element/Dialog'
import BtnPrimary from '../../../../../../../common/element/Buttons/btnPrimary'
import ThreeDotLoader from "../../../../../../../common/element/ButtonLoader/ThreeDotLoader"
import Select from '../../../../../../../common/element/Select';
// Import the CommonPhoneInput component
import CommonPhoneInput from '../../../../../../../common/CommonPhoneInput/CommonPhoneInput';

const AddEditEmployee = ({ dialogOpen, onDialogClose, employeeData, isEdit, onSubmit, loading }) => {
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const initialFormData = {
    name: "",
    email: "",
    phoneNumber: "",
    gender: ""
  };

  const [formData, setFormData] = useState(initialFormData);

  const initialFormErrors = {
    name: "",
    email: "",
    phoneNumber: "",
    gender: ""
  };
  const [formError, setFormErrors] = useState(initialFormErrors);

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" }
  ];

  const findOptionByValue = (options, value) =>
    options.find(option => option.value === value) || null;

  useEffect(() => {
    if (isEdit && employeeData) {
      const user = employeeData.user || {};
      setFormData({
        name: employeeData.name || employeeData.employeeName || [user.first_name, user.last_name].filter(Boolean).join(" ") || "",
        email: employeeData.email || employeeData.emailAddress || user.email || "",
        phoneNumber: employeeData.phoneNumber || employeeData.phone || employeeData.phone_number || user.mobile_number || "",
        gender: findOptionByValue(
          genderOptions,
          employeeData.gender || employeeData.gender?.value || employeeData.gender?.label || user.gender
        ),
      });
    } else {
      setFormData(initialFormData);
    }
  }, [isEdit, employeeData, dialogOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formError[field]) setFormErrors(prev => ({ ...prev, [field]: "" }));
  };

  // **New Handler for Phone Number Validation**
  const handlePhoneChange = (phone, country) => {
    setFormData(prev => ({ ...prev, phoneNumber: phone }));

    // Real-time validation for phone length
    if (phone && country && country.format) {
      // Count the number of digits required by the country format
      const requiredLength = (country.format.match(/\./g) || []).length;
      
      if (phone.length < requiredLength) {
        setFormErrors(prev => ({ ...prev, phoneNumber: "Invalid phone number" }));
      } else {
        setFormErrors(prev => ({ ...prev, phoneNumber: "" }));
      }
    } else {
      // Clear error if empty (handled by required check later) or valid
      setFormErrors(prev => ({ ...prev, phoneNumber: "" }));
    }
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = "Name is required";
    
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Enter valid email";
    }

    // Phone Validation logic for Submit
    if (!formData.phoneNumber) {
      errors.phoneNumber = "Phone number is required";
    } else if (formError.phoneNumber) {
      // If there is already a length error from handlePhoneChange, preserve it
      errors.phoneNumber = formError.phoneNumber;
    }

    if (!formData.gender) errors.gender = "Gender is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setButtonDisabled(true);
    const payload = {
      name: formData.name,
      email: formData.email,
      mobile_number: formData.phoneNumber,
      gender: formData.gender?.value || formData.gender,
    };
    onSubmit?.(payload);
  };

  useEffect(() => {
    if (!loading) {
      setButtonDisabled(false);
    }
  }, [loading]);

  const handleDialogClose = () => {
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
    onDialogClose();
  };

  return (
    <Dialog isOpen={dialogOpen} onClose={handleDialogClose} width={700}>
      <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
        <div className="flex items-center">
          <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.6609 14.3594H9.24008V15.1924H11.6609V14.3594Z" fill="#344054" />
              <path d="M16.4294 12.129C15.9623 11.969 15.2591 11.6721 14.9508 11.4433V10.6699C16.0017 10.0684 16.7273 9.01454 16.9879 7.71397C17.6596 7.55356 18.1315 6.89416 18.1315 6.29619C18.1315 5.73704 17.7356 5.34284 17.0934 5.2263V4.75075C17.0991 4.57589 17.1373 2.50437 15.8307 1.14408C15.1016 0.384913 14.1065 0 12.8734 0C11.6423 0 10.6537 0.37804 9.92769 1.12483C9.20277 0.378165 8.21705 0 6.98959 0C5.75254 0 4.75541 0.38583 4.02595 1.14678C2.72309 2.50579 2.76345 4.5716 2.76958 4.75087V5.2263C2.12737 5.34284 1.73151 5.73704 1.73151 6.29619C1.73151 6.89433 2.20364 7.55398 2.87567 7.71414C3.13225 8.98346 3.8326 10.022 4.84155 10.6297V11.3114C4.5399 11.5736 3.92365 11.9379 3.54265 12.1394C0.0781822 13.2711 0.00162446 15.3681 8.33056e-05 15.4571C4.16528e-05 15.4595 0 15.462 0 15.4644V16.3322C0 16.5623 0.186521 16.7487 0.416528 16.7487H19.5835C19.8135 16.7487 20 16.5623 20 16.3322V15.4626C19.9996 15.3726 19.95 13.2463 16.4294 12.129ZM17.0934 6.08235C17.207 6.12171 17.2984 6.18714 17.2984 6.29619C17.2984 6.42736 17.2185 6.59538 17.0921 6.72384C17.0925 6.69756 17.0934 6.08235 17.0934 6.08235ZM2.56456 6.29619C2.56456 6.18714 2.65599 6.12171 2.76958 6.08235C2.76958 6.08235 2.77053 6.69756 2.77091 6.72384C2.64454 6.59538 2.56456 6.42736 2.56456 6.29619ZM16.2604 6.64495C16.2604 6.84897 16.2462 7.05973 16.2184 7.27016C16.047 8.53899 15.3666 9.5504 14.3518 10.045C14.3501 10.0458 14.3485 10.0466 14.3468 10.0475C13.9083 10.2686 13.4126 10.3807 12.8734 10.3807C12.3343 10.3807 11.8386 10.2686 11.4001 10.0475C11.398 10.0464 11.3958 10.0454 11.3937 10.0444C11.0417 9.87433 10.7271 9.63612 10.4544 9.33643C10.763 8.86142 10.9849 8.31094 11.107 7.71434C11.7794 7.55448 12.2519 6.89458 12.2519 6.29623C12.2519 5.73709 11.8561 5.34289 11.2138 5.22634V4.88208C11.8466 4.74608 12.5314 4.52886 12.881 4.13229C13.4961 4.47901 14.7291 5.05523 16.2604 5.1472V6.64495ZM14.1178 11.0332V11.474C14.0245 11.5701 13.8481 11.7239 13.5921 11.8275C13.4128 11.9007 13.2171 11.9378 13.0104 11.9378C12.7429 11.9378 12.4661 11.8767 12.1889 11.7567C11.944 11.6497 11.749 11.5195 11.629 11.4298V11.0332C12.02 11.1529 12.4362 11.2137 12.8734 11.2137C13.3106 11.2137 13.7268 11.1529 14.1178 11.0332ZM11.5092 12.3453L11.4464 12.5294C11.2948 12.4588 11.1366 12.3915 10.9709 12.3272C11.0587 12.2755 11.1442 12.2216 11.2241 12.1648C11.3049 12.2212 11.4006 12.2832 11.5092 12.3453ZM10.7959 10.6701V11.4455C10.611 11.589 10.3121 11.7413 10.0824 11.8585C10.0572 11.8713 10.0323 11.884 10.0079 11.8965C9.62342 11.6739 9.31635 11.4673 9.13762 11.3116V10.6301C9.42927 10.4549 9.69468 10.2435 9.93156 9.99779C10.191 10.2648 10.4802 10.4896 10.7959 10.6701ZM11.2125 6.72384C11.2128 6.69756 11.2138 6.08235 11.2138 6.08235C11.3274 6.12171 11.4188 6.18719 11.4188 6.29619C11.4188 6.42736 11.3388 6.59538 11.2125 6.72384ZM12.8734 0.833055C13.8691 0.833055 14.6608 1.13066 15.2265 1.71764C16.0014 2.52158 16.1975 3.69344 16.2459 4.31065C14.3571 4.17519 12.9546 3.20139 12.9402 3.19119C12.8131 3.10097 12.6463 3.08914 12.5079 3.16065C12.3694 3.23213 12.2824 3.37496 12.2824 3.53078C12.2824 3.5851 12.0723 3.82114 11.1755 4.0354C11.1107 3.44198 10.9311 2.57668 10.4459 1.79257C11.0194 1.15624 11.8343 0.833055 12.8734 0.833055ZM4.62733 1.72326C5.1936 1.13254 5.98842 0.833014 6.98963 0.833014C8.13279 0.833014 9.00533 1.22638 9.58276 2.00212C10.1701 2.79174 10.3242 3.79457 10.364 4.3104C8.47555 4.17432 7.07052 3.20106 7.05606 3.19085C6.92898 3.1008 6.7622 3.08918 6.62396 3.16065C6.48559 3.23217 6.3987 3.37492 6.3987 3.53066C6.3987 3.5908 6.13733 3.88396 4.92965 4.11567C4.44585 4.20851 3.9566 4.26333 3.61646 4.29382C3.66453 3.67544 3.8598 2.52391 4.62733 1.72326ZM3.64474 7.27137C3.61679 7.06002 3.60263 6.84926 3.60263 6.64495V5.13162C3.96118 5.10155 4.51695 5.04274 5.07881 4.93535C5.48467 4.8578 6.52274 4.65945 6.99334 4.12958C7.60776 4.47592 8.84509 5.0549 10.3807 5.14712V6.64491C10.3807 6.84892 10.3665 7.05969 10.339 7.26853C10.2409 7.97438 9.98271 8.61213 9.59255 9.11275C9.59097 9.11479 9.58943 9.11684 9.58789 9.11888C9.29694 9.50683 8.93381 9.81223 8.505 10.0285C8.05273 10.2622 7.54286 10.3807 6.98959 10.3807C6.43627 10.3807 5.92644 10.2622 5.47147 10.0271C4.48196 9.52507 3.81598 8.51987 3.64474 7.27137ZM8.30457 11.0118V11.3225C8.18552 11.4403 7.95972 11.6379 7.66224 11.7787C7.21726 11.9899 6.7622 11.99 6.31656 11.7785C6.01991 11.6381 5.79382 11.4402 5.67461 11.3224V11.0116C6.08718 11.1457 6.5272 11.2138 6.98963 11.2138C7.45243 11.2138 7.89283 11.1456 8.30457 11.0118ZM0.833056 15.9157V15.4738C0.836013 15.4293 0.870918 15.0546 1.22097 14.5711C1.85122 13.7006 3.87729 12.9046 3.89816 12.8937C3.99963 12.8407 4.73742 12.4502 5.24317 12.0575C5.34843 12.149 5.47901 12.2508 5.63079 12.3481L5.21172 13.6054C5.18323 13.6908 5.18323 13.7832 5.21168 13.8686L5.89295 15.9157H0.833056ZM7.20831 15.9157H6.77095L6.0459 13.7372L6.39479 12.6906C6.78832 12.7949 7.1911 12.7949 7.58443 12.6906L7.93331 13.7372L7.20831 15.9157ZM13.1461 15.7414L13.088 15.9157H8.08626L8.76754 13.8686C8.79599 13.7832 8.79599 13.6908 8.76745 13.6053L8.34834 12.3481C8.49992 12.2509 8.63037 12.1493 8.73551 12.0579C9.04944 12.3016 9.45997 12.5473 9.79499 12.7335C9.96551 12.8316 10.0816 12.8918 10.0864 12.8944C10.1074 12.9052 10.1292 12.9143 10.1517 12.9215C10.6652 13.0851 11.1006 13.2684 11.4838 13.4827C13.0724 14.3633 13.1432 15.402 13.1461 15.4697V15.7414ZM13.6142 14.3428C13.359 13.9052 12.9255 13.3979 12.1891 12.9326L12.2788 12.6696C12.5233 12.7368 12.7681 12.7709 13.0104 12.7709C13.1709 12.7709 13.328 12.7557 13.4803 12.7259L13.817 13.7366L13.6142 14.3428ZM19.1669 15.9157H13.9792V15.8786L14.6511 13.8692C14.6797 13.7837 14.6798 13.6912 14.6514 13.6056L14.2575 12.4209C14.3808 12.3451 14.486 12.2656 14.5722 12.1908C15.1233 12.5526 15.9661 12.851 16.1644 12.9187C16.1674 12.9197 18.164 13.6937 18.7881 14.5646C19.1396 15.055 19.1653 15.4349 19.1669 15.4701V15.9157Z" fill="#344054" />
              <path d="M17.5405 14.3594H15.1198V15.1924H17.5405V14.3594Z" fill="#344054" />
            </svg>

          </span>
          <h5 className="text-gray-900 text-base font-semibold">
            {isEdit ? "Edit Employee" : "Add Employee"}
          </h5>
        </div>

        <button onClick={handleDialogClose} className="text-gray-400 hover:text-gray-600">
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="px-6">
        {/* Manual Input Fields Only */}
        <div className="flex mb-5">
          <label className="font-large text-gray-700 w-1/4">Name*</label>
          <div className="w-3/4 pl-2">
            <input
              className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={formData.name}
              placeholder="Enter Name"
              onChange={(e) => handleInputChange("name", e.target.value)}
            />
            {formError.name && <p className="text-xs text-red-600 mt-1">{formError.name}</p>}
          </div>
        </div>

        <div className="flex mb-5">
          <label className="font-large text-gray-700 w-1/4">Email*</label>
          <div className="w-3/4 pl-2">
            <input
              type="email"
              className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={formData.email}
              placeholder="Enter Email"
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
            {formError.email && <p className="text-xs text-red-600 mt-1">{formError.email}</p>}
          </div>
        </div>

        <div className="flex mb-5">
          <label className="font-large text-gray-700 w-1/4">Phone*</label>
          <div className="w-3/4 pl-2">
            {/* Replaced Input with CommonPhoneInput */}
            <CommonPhoneInput
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              placeholder="Enter Phone"
              error={formError.phoneNumber}
            />
          </div>
        </div>

        <div className="flex mb-5">
          <label className="font-large text-gray-700 w-1/4">Gender*</label>
          <div className="w-3/4 pl-2">
            <Select
              options={genderOptions}
              value={formData.gender}
              onChange={(option) => handleInputChange("gender", option)}
              placeholder="Select Gender"
            />
            {formError.gender && <p className="text-xs text-red-600 mt-1">{formError.gender}</p>}
          </div>
        </div>
      </div>

      <div className="flex p-6 border-t">
        <button
          className="mr-2 w-full rounded-lg h-10 bg-orange-50 text-gray-700 text-sm font-semibold hover:bg-orange-100 border border-gray-200"
          onClick={handleDialogClose}
        >
          Cancel
        </button>
        <BtnPrimary
          disabled={buttonDisabled || loading}
          onClick={handleSubmit}
          className="ml-2"
          style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
        >
          {buttonDisabled || loading ? <ThreeDotLoader /> : (isEdit ? "Update Employee" : "Add Employee")}
        </BtnPrimary>
      </div>
    </Dialog>
  );
};

export default AddEditEmployee;